import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Edit2, Save, X, DollarSign, Package } from 'lucide-react';

export function RecipeEditor() {
  const [drinks, setDrinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchAllDrinks();
  }, []);

  const fetchAllDrinks = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/fizze/admin/all-drinks`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch drinks');
      
      const data = await response.json();
      setDrinks(data.drinks || []);
    } catch (error) {
      console.error('Error fetching drinks:', error);
      toast.error("Failed to load drinks menu");
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (drink) => {
    setEditingId(drink.id);
    setEditForm({
      name: drink.name,
      recipe: drink.recipe,
      flavor_profile: drink.flavor_profile,
      price: drink.price,
      available: drink.available,
      category: drink.category
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveRecipe = async (drinkId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/fizze/admin/drink/${drinkId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editForm)
      });

      if (!response.ok) throw new Error('Failed to update drink');

      const data = await response.json();
      
      // Update local state
      setDrinks(drinks.map(d => d.id === drinkId ? data.drink : d));
      
      toast.success(`✅ ${editForm.name} has been updated successfully.`);
      
      cancelEdit();
    } catch (error) {
      console.error('Error updating recipe:', error);
      toast.error("Failed to update recipe. Please try again.");
    }
  };

  const groupedDrinks = drinks.reduce((acc, drink) => {
    if (!acc[drink.category]) {
      acc[drink.category] = [];
    }
    acc[drink.category].push(drink);
    return acc;
  }, {});

  if (loading) {
    return <div className="text-center py-8">Loading recipes...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold">Recipe Editor</h2>
          <p className="text-gray-600">Edit how each Fizze drink is made</p>
        </div>
        <Button onClick={fetchAllDrinks} variant="outline">
          🔄 Refresh Menu
        </Button>
      </div>

      {Object.entries(groupedDrinks).map(([category, categoryDrinks]) => (
        <div key={category} className="space-y-3">
          <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Package className="w-6 h-6" />
            {category}
            <Badge variant="secondary">{categoryDrinks.length} items</Badge>
          </h3>

          <div className="grid gap-4">
            {categoryDrinks.map((drink) => (
              <Card key={drink.id} className="p-6">
                {editingId === drink.id ? (
                  // EDIT MODE
                  <div className="space-y-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 space-y-4">
                        <div>
                          <label className="block text-sm font-semibold mb-1">Drink Name</label>
                          <Input
                            value={editForm.name}
                            onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                            className="max-w-md"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold mb-1">Flavor Profile</label>
                          <Input
                            value={editForm.flavor_profile}
                            onChange={(e) => setEditForm({...editForm, flavor_profile: e.target.value})}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold mb-1">
                            📝 Recipe Instructions (How to Make It)
                          </label>
                          <Textarea
                            value={editForm.recipe}
                            onChange={(e) => setEditForm({...editForm, recipe: e.target.value})}
                            rows={4}
                            className="font-mono text-sm"
                            placeholder="Enter step-by-step recipe..."
                          />
                        </div>

                        <div className="flex gap-4">
                          <div className="flex-1">
                            <label className="block text-sm font-semibold mb-1">Price</label>
                            <div className="flex items-center gap-2">
                              <DollarSign className="w-4 h-4 text-gray-500" />
                              <Input
                                type="number"
                                step="0.01"
                                value={editForm.price || ''}
                                onChange={(e) => setEditForm({...editForm, price: parseFloat(e.target.value)})}
                                className="max-w-[120px]"
                              />
                            </div>
                          </div>

                          <div className="flex items-end">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={editForm.available}
                                onChange={(e) => setEditForm({...editForm, available: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm font-semibold">Available on Menu</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-4 border-t">
                      <Button onClick={() => saveRecipe(drink.id)} className="bg-green-600 hover:bg-green-700">
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </Button>
                      <Button onClick={cancelEdit} variant="outline">
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  // VIEW MODE
                  <div>
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="text-xl font-bold">{drink.name}</h4>
                          {drink.available ? (
                            <Badge className="bg-green-600">Available</Badge>
                          ) : (
                            <Badge variant="secondary">Unavailable</Badge>
                          )}
                          {drink.price && (
                            <Badge variant="outline">${drink.price.toFixed(2)}</Badge>
                          )}
                        </div>
                        <p className="text-gray-600 italic">{drink.flavor_profile}</p>
                      </div>
                      <Button onClick={() => startEdit(drink)} size="sm">
                        <Edit2 className="w-4 h-4 mr-1" />
                        Edit Recipe
                      </Button>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <p className="text-sm font-semibold text-gray-700 mb-1">📝 Recipe:</p>
                      <p className="text-gray-800 font-mono text-sm">{drink.recipe}</p>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
